import { useRef } from 'react';
import { Link } from '@tanstack/react-router';
import type { FichaAfetadaImpacto, ImpactoCascataResumo } from '@triade/contracts';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatarContagem, formatarCusto } from '@/lib/format';

/** Rotulo do nivel: 0 = a propria ficha (a que muda); 1+ = pela cascata. Sem calculo: so o numero que a API mandou. */
export function rotuloNivelImpacto(nivel: number): string {
  return nivel === 0 ? 'própria ficha' : 'pela cascata';
}

/** Cabeçalho: contagem de fichas escolhidas que mudam de custo (o "e mais M" fica na ultima linha da tabela). */
export function resumoImpacto(total: number): string {
  if (total === 0) return 'Nenhuma ficha escolhida muda de custo.';
  if (total === 1) return '1 ficha escolhida muda de custo.';
  return `${formatarContagem(total)} fichas escolhidas mudam de custo.`;
}

/** Itens com a marca "revisar preço" que a gravacao vai criar ou renovar. Vazio quando nao ha nenhum. */
export function revisarPrecoImpacto(itens: number): string | null {
  if (itens <= 0) return null;
  return itens === 1
    ? '1 item com preço ficará marcado para revisar preço.'
    : `${formatarContagem(itens)} itens com preço ficarão marcados para revisar preço.`;
}

export interface ImpactoCascataDialogoProps {
  impacto: ImpactoCascataResumo;
  /** Fecha sem gravar (botao Cancelar, Esc ou clique fora). */
  onCancelar: () => void;
  /** Grava (a tela chama a rota de gravacao de sempre). */
  onConfirmar: () => void;
}

/**
 * Confirmacao antes de gravar: mostra o que a API calculou na previa (nada e recalculado aqui). As colunas de custo so
 * aparecem se a API as mandou (`custo.ver`). Radix cuida do foco (entra no Cancelar), do Esc e do papel dialog.
 */
export function ImpactoCascataDialogo({ impacto, onCancelar, onConfirmar }: ImpactoCascataDialogoProps) {
  const cancelarRef = useRef<HTMLButtonElement>(null);
  const linhas: FichaAfetadaImpacto[] = impacto.fichasAfetadas;
  const comCusto = linhas.some((f) => f.custoAntes !== undefined);
  const revisar = revisarPrecoImpacto(impacto.itensRevisarPreco);

  return (
    <Dialog open onOpenChange={(aberto) => { if (!aberto) onCancelar(); }}>
      <DialogContent
        showCloseButton={false}
        className="max-h-[90vh] w-[min(960px,96vw)] max-w-none grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden sm:max-w-none"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          cancelarRef.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>Impacto da mudança</DialogTitle>
          <DialogDescription>{resumoImpacto(impacto.totalFichasAfetadas)}</DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-col gap-3">
          {linhas.length > 0 && (
            <div className="min-h-0 overflow-auto">
              <table className="w-full text-small" aria-label="Fichas escolhidas que mudam de custo" data-testid="impacto-tabela">
                <thead>
                  <tr className="text-left text-caption text-ink-muted">
                    <th scope="col" className="pb-1 font-medium">Item</th>
                    <th scope="col" className="pb-1 font-medium">Versão</th>
                    <th scope="col" className="pb-1 font-medium">Nível</th>
                    {comCusto && (
                      <th scope="col" className="pb-1 text-right font-medium">Custo antes</th>
                    )}
                    {comCusto && (
                      <th scope="col" className="pb-1 text-right font-medium">Custo depois</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((f) => (
                    <tr key={`${f.fichaId}-${f.nivel}`} className="border-t border-border" data-testid="impacto-linha">
                      <td className="py-1.5">
                        <Link to="/cadastros/itens/$id" params={{ id: f.itemId }} target="_blank" rel="noreferrer" className="font-medium text-ink hover:underline">
                          <span className="tabular">{f.itemCodigo}</span> · {f.itemDescricao}
                        </Link>
                      </td>
                      <td className="tabular py-1.5">v{f.versao}</td>
                      <td className="py-1.5 text-ink-secondary">{rotuloNivelImpacto(f.nivel)}</td>
                      {comCusto && (
                        <td className="tabular py-1.5 text-right">{formatarCusto(f.custoAntes ?? null)}</td>
                      )}
                      {comCusto && (
                        <td className="tabular py-1.5 text-right">{formatarCusto(f.custoDepois ?? null)}</td>
                      )}
                    </tr>
                  ))}
                  {impacto.maisN > 0 && (
                    <tr className="border-t border-border text-ink-secondary" data-testid="impacto-mais">
                      <td colSpan={comCusto ? 5 : 3} className="py-1.5">e mais {formatarContagem(impacto.maisN)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {impacto.truncado && (
            <p role="status" className="rounded-xl border border-warning-dot/40 bg-warning-bg px-4 py-2.5 text-small text-warning-ink">
              A cascata foi cortada no limite de níveis: o total pode estar incompleto.
            </p>
          )}
          {revisar && <p className="text-small text-ink-secondary">{revisar}</p>}
        </div>

        <DialogFooter>
          <Button ref={cancelarRef} type="button" variant="outline" onClick={onCancelar}>
            Cancelar
          </Button>
          <Button type="button" onClick={onConfirmar}>
            Confirmar e salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
